import { ReactElement } from "react";
import type { GetStaticProps } from "next";
import NextHeadSeo from "next-head-seo";

import LayoutMain from "~/components/layouts/LayoutMain";
import LayoutIntroSlideshow from "~/components/layouts/LayoutIntroSlideshow";
import { Header } from "~/components/app/Header";
import dynamic from "next/dynamic";
import {
  restApiGetPostBySlugOrFallbackId,
  restApiESGetSettings,
} from "~/utils/restApi";
import { SocialMediaIcons } from "~/components//ui/SocialMediaIcons";
import { FlexibleRow } from "~/components/flexibleContent/FlexibleRow";
import { appConfig } from "~/config";
import { PageSubMenu } from "~/components/ui/PageSubMenu";
import styled from "styled-components";
import { Footer } from "~/components/app/Footer";
import { safeAnchorId } from "~/utils/safeAnchorId";
import PageMargins from "~/components/styled/PageMargins";
import { Heading } from "~/components/ui/Heading";
import { NewsletterForm } from "../components/app/NewsletterForm";
import { InstagramPosts } from "~/components/content/InstagramPosts";

const Social = styled.div`
  display: flex;
  
  flex-direction: column;

  ${(props) =>
    props.theme.apply(["base", "mobile", "tablet"], (breakpoint: string) => {
      return `
          margin-bottom: ${props.theme.spaceFontBottom(breakpoint, 7, "body")};
        `;
    })}

  ${({ theme }) => theme.breakpoints.tablet} {
    margin-bottom: 0;
  }

  ${({ theme }) => theme.breakpoints.tabletLandscape} {
    flex-direction: column;
    margin-bottom: 0;
  }
`;

const Newsletter = styled.div`
  width: 100%;
  
  ${(props) =>
    props.theme.apply(
      ["tabletLandscape", "desktop", "screen"],
      (breakpoint: string) => {
        return `
        padding-top: ${props.theme.spacePx(breakpoint, 8)};
      `;
      }
    )}
`;
const TwoCol = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${(props) =>
    props.theme.apply(["base", "mobile", "tablet"], (breakpoint: string) => {
      return `
          margin-top: ${props.theme.spacePx(breakpoint, 7)};
          gap: ${props.theme.spacePx(breakpoint, 6)};
          padding-bottom: ${props.theme.spacePx(breakpoint, 6)};
        `;
    })}

  ${(props) =>
    props.theme.apply(
      ["tabletLandscape", "desktop", "screen"],
      (breakpoint: string) => {
        return `
        display: grid;
        grid-template-columns: 1fr 1fr;
        margin-top: ${props.theme.spacePx(breakpoint, 6)};
        gap: ${props.theme.spacePx(breakpoint, 6)};
        padding-bottom: ${props.theme.spacePx(breakpoint, 9)};
      `;
      }
    )}
`;

const MicroPageIntro = dynamic(
  () => import("~/components/content/MicroPageIntro")
);

const MicroPageIntroContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: #000;

  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
        margin-bottom: ${props.theme.spacePx(breakpoint, 7)};
      `;
    })}
`;

const Links = styled.div``;

export const Block = styled.div`
  width: 100%;

  ${(props) =>
    props.theme.apply(["base", "mobile", "tablet"], (breakpoint: string) => {
      return `
          margin-top: ${props.theme.spacePx(breakpoint, 6)};
        `;
    })}

  ${(props) =>
    props.theme.apply(
      ["tabletLandscape", "desktop", "screen"],
      (breakpoint: string) => {
        return `
          margin-top:0;
          margin-bottom: ${props.theme.spacePx(breakpoint, 7)};
        `;
      }
    )}
`;

export const BlockHeading = styled.div`
  width: 100%;
  
  ${(props) =>
    props.theme.apply(
      ["tabletLandscape", "desktop", "screen"],
      (breakpoint: string) => {
        return `
          padding-right: ${props.theme.spacePx(breakpoint, 2)};
        `;
      }
    )}
`;

const Page = ({ data }: { data: any }) => {
  

  const menuItems = data?.acf?.is_microsite
    ? data?.acf?.content?.length > 0
      ? data?.acf?.content.reduce((carry: any, row: any) => {
          if (row?.acf_fc_layout === "subpage") {
            carry.push({
              anchor: `#${safeAnchorId(row?.name)}`,
              title: row?.name,
            });
          }
          return carry;
        }, [])
      : []
    : data?.subMenu?.menu;

  return (
    <>
      <NextHeadSeo
        canonical={data?.yoast_head_json?.canonical}
        title={data?.yoast_head_json?.title ?? data?.title}
        description={data?.yoast_head_json?.description}
        og={{
          title: data?.yoast_head_json?.og_title,
          type: data?.yoast_head_json?.og_type,
          siteName: data?.yoast_head_json?.og_site_name,
          image: data?.yoast_head_json?.twitter_image,
        }}
        twitter={{
          card: "summary_large_image",
        }}
      />
      <Header
        hasHero={data?.acf?.is_microsite}
        showMainMenu={!data?.acf?.is_microsite}
      >
        <PageSubMenu title={data?.title} menu={menuItems} />
      </Header>

      {data?.acf?.is_microsite && (
        <Header isOverlay showMainMenu={!data?.acf?.is_microsite} />
      )}

      <div id="content" style={{ position: "absolute" }} />
      {data?.acf?.is_microsite && (
        <MicroPageIntroContainer>
          <MicroPageIntro data={data} menuItems={menuItems} />
        </MicroPageIntroContainer>
      )}
      <main>
        <PageMargins>
          <Newsletter>
            <Block>
              <BlockHeading>
                <Heading heading="h2" space={7}>
                  STAY IN TOUCH – SUBSCRIBE TO IKON’S NEWSLETTER OR FOLLOW US ON SOCIAL MEDIA.
                </Heading>
              </BlockHeading>
              <TwoCol>
                <Block>
                    <Heading heading="h4" space={8}>
                      SUBSCRIBE TO OUR NEWSLETTER
                    </Heading>
                    <NewsletterForm />
                </Block>
              <Social>
                  <Heading heading="h4" space={8}>
                  SOCIAL MEDIA
                </Heading>
                  <div>
                    <Links>
                      <SocialMediaIcons />
                    </Links>
                  </div>
              </Social>
              </TwoCol>
            
            </Block>
          </Newsletter>
        </PageMargins>
        {data?.acf?.content?.length > 0 ? (
          <FlexibleRow
            field="content"
            content={data?.acf?.content}
            fontStyle={data?.acf?.font_style ?? "default"}
          />
        ) : (
          <div />
        )}
        <InstagramPosts />
      </main>
      {data?.acf?.is_microsite && <Footer />}
    </>
  );
};


export const getStaticProps: GetStaticProps = async (context) => {
  const token = (context?.previewData as any)?.token;
  
  const data = await restApiGetPostBySlugOrFallbackId(
    "page",
    "connect",
    token
  );

  const frontendSettings = await restApiESGetSettings();
  if (!data)
    return {
      props: {
        frontendSettings,
      },
      notFound: true,
      revalidate: frontendSettings?.options?.copyrightNotice ? 240 : 15,
    };

  return {
    props: {
      frontendSettings,
      data: data ?? null,
    },
    revalidate: frontendSettings?.options?.copyrightNotice
      ? appConfig.revalidateInterval("page", {
          date: data?.date,
          modified: data?.modified,
        })
      : 15,
  };
};

Page.getLayout = function getLayout(page: ReactElement, props: any) {
  if (props?.data?.acf?.is_microsite)
    return <LayoutIntroSlideshow props={props}>{page}</LayoutIntroSlideshow>;
  return <LayoutMain props={props}>{page}</LayoutMain>;
};
export default Page;
